import { useTranslation } from 'react-i18next';

import { StatusDot, StatusDotVariant } from '@/components/ui/custom/StatusDot';
import { Webhook, WebhookType } from '@/shared/types/webhook';

type WebhookStatus = 'enabled' | 'disabled' | 'used_in_dialog' | 'not_used_in_dialog';

const statusVariant: Record<WebhookStatus, StatusDotVariant> = {
  enabled: 'successful',
  disabled: 'neutral',
  used_in_dialog: 'successful',
  not_used_in_dialog: 'neutral',
};

interface WebhookStatusDotProps {
  webhook: Webhook;
  isUsedInDialog: boolean;
  className?: string;
}

/**
 * Event webhook types that show enabled/disabled status
 */
export const EVENT_WEBHOOK_TYPES = [
  WebhookType.FIRST_MESSAGE,
  WebhookType.PRE_MESSAGE,
  WebhookType.POST_MESSAGE,
  WebhookType.LOG_MESSAGE,
  WebhookType.PRE_NBA,
];

/**
 * Determines webhook status based on type:
 * - Event webhooks (pre/post/first_message): show enabled/disabled based on webhook.enabled
 * - Action webhooks: show used/not used in dialog flow based on isUsedInDialog
 * @param webhook - Webhook object
 * @param isUsedInDialog - Whether webhook is used in any dialog
 * @returns Status string
 */
const getWebhookStatus = (webhook: Webhook, isUsedInDialog: boolean): WebhookStatus => {
  // Event webhooks: show enabled/disabled status
  if (EVENT_WEBHOOK_TYPES.includes(webhook.type)) {
    return webhook.enabled ? 'enabled' : 'disabled';
  }

  // Action webhooks: show dialog usage status
  return isUsedInDialog ? 'used_in_dialog' : 'not_used_in_dialog';
};

/**
 * Status dot component for webhooks
 * Shows visual status based on webhook type:
 * - Event webhooks (pre/post/first_message): Enabled (green) or Disabled (grey)
 * - Action webhooks: Used in dialog flow (green) or Not used in dialog flow (grey)
 */
export function WebhookStatusDot({ webhook, isUsedInDialog, className }: WebhookStatusDotProps) {
  const { t } = useTranslation();
  const status = getWebhookStatus(webhook, isUsedInDialog);
  const tooltip: Record<WebhookStatus, string> = {
    enabled: t('common.active'),
    disabled: t('common.inactive'),
    used_in_dialog: t('webhook.status.used_in_dialog'),
    not_used_in_dialog: t('webhook.status.not_used_in_dialog'),
  };

  return (
    <StatusDot variant={statusVariant[status]} tooltip={tooltip[status]} className={className} />
  );
}
